/**
 * 📄 HOOK GESTION FACTURES FOURNISSEURS LOGISTICS
 * ================================================
 * Hook custom pour la saisie et gestion des factures fournisseurs
 *
 * Workflow:
 * 1. Utilisateur sélectionne les coûts et débours non facturés fournisseur
 * 2. Clique "Saisir Facture Fournisseur" → Modal de saisie
 * 3. Validation → Création facture avec items sélectionnés
 * 4. Marquage automatique des lignes (is_supplier_billed = 1)
 * 5. Support multi-dossiers (une facture peut couvrir plusieurs shipments)
 *
 * Différences avec factures clients:
 * - ✅ Support multi-dossiers (is_multi_shipment)
 * - ✅ Saisie manuelle uniquement (pas de génération auto)
 * - ✅ Unmarking automatique à la suppression
 * - ✅ Validation côté backend (fournisseur unique, coûts et débours, devises et portée)
 *
 * @see routes/logistics/supplier-bills.js (backend PHASE 2)
 * @see PHASE-2-API-BACKEND-SUPPLIER-BILLING-COMPLETE.md (documentation)
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { message } from 'antd';
import API_CONFIG from '../../config/api';
import { SupplierBill, RecordSupplierBillRequest } from '../types/invoice.types';
import { useSelection } from '../../SelectionContext';

export const useSupplierBills = () => {
  const [loading, setLoading] = useState(false);
  const [supplierBills, setSupplierBills] = useState<SupplierBill[]>([]);
  const [currentSupplierBill, setCurrentSupplierBill] = useState<SupplierBill | null>(null);

  const { selectedCompany } = useSelection();

  /**
   * Récupérer toutes les factures fournisseurs d'un dossier spécifique
   * @param shipmentId - ID du dossier d'expédition
   */
  const fetchSupplierBillsByShipment = useCallback(async (shipmentId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/supplier-bills`,
        {
          params: { shipment_id: shipmentId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        const billsList = response.data.data || [];
        setSupplierBills(billsList);
        console.log('✅ [useSupplierBills] Factures fournisseurs chargées:', billsList.length);
        return billsList;
      } else {
        message.error(response.data.message || 'Erreur lors du chargement des factures fournisseurs');
        return [];
      }
    } catch (error: any) {
      console.error('❌ [useSupplierBills] Erreur fetchSupplierBillsByShipment:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion au serveur');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupérer les détails complets d'une facture fournisseur (avec items et payments)
   * @param billId - ID de la facture fournisseur
   */
  const fetchSupplierBillById = useCallback(async (billId: number): Promise<SupplierBill | null> => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/supplier-bills/${billId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        const bill = response.data.data;
        setCurrentSupplierBill(bill);
        console.log('✅ [useSupplierBills] Facture fournisseur détaillée chargée:', bill.bill_number);
        return bill;
      } else {
        message.error(response.data.message || 'Erreur lors du chargement de la facture');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [useSupplierBills] Erreur fetchSupplierBillById:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enregistrer une facture fournisseur (workflow multi-dossiers)
   * Endpoint principal: POST /api/logistics/supplier-bills/record
   *
   * Validations automatiques backend:
   * - Les lignes doivent être de type cost ou disbursement
   * - Toutes lignes doivent avoir le même supplier_id
   * - Aucune ligne ne doit avoir is_supplier_billed=1 déjà
   * - Transaction atomique (rollback en cas d'erreur)
   *
   * @param data - Données de la facture (supplier_id, job_costing_line_ids, bill_number, etc.)
   */
  const recordSupplierBill = useCallback(async (
    data: RecordSupplierBillRequest
  ): Promise<SupplierBill | null> => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_CONFIG.API_URL}/logistics/supplier-bills/record`,
        {
          ...data,
          company_id: selectedCompany, // SuperAdmin context
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const newBill = response.data.data;
        message.success(
          response.data.message || `Facture ${newBill.bill_number} enregistrée avec succès`
        );

        // Ajouter la nouvelle facture à la liste
        setSupplierBills((prev) => [newBill, ...prev]);
        setCurrentSupplierBill(newBill);

        console.log('✅ [useSupplierBills] Facture créée:', newBill.bill_number);
        return newBill;
      } else {
        message.error(response.data.message || 'Erreur lors de l\'enregistrement');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [useSupplierBills] Erreur recordSupplierBill:', error);
      message.error(
        error.response?.data?.message || 'Erreur lors de l\'enregistrement de la facture fournisseur'
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  /**
   * Supprimer une facture fournisseur
   * IMPORTANT: Unmarking automatique des lignes (is_supplier_billed → 0)
   *
   * @param billId - ID de la facture à supprimer
   */
  const deleteSupplierBill = useCallback(async (billId: number): Promise<boolean> => {
    setLoading(true);
    try {
      console.log('🗑️ [useSupplierBills] Suppression facture fournisseur:', billId);

      const response = await axios.delete(
        `${API_CONFIG.API_URL}/logistics/supplier-bills/${billId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        message.success('Facture fournisseur supprimée avec succès');

        // Retirer la facture de la liste
        setSupplierBills((prev) => prev.filter((bill) => bill.id !== billId));

        console.log('✅ [useSupplierBills] Facture supprimée (lignes unmarked automatiquement)');
        return true;
      } else {
        message.error(response.data.message || 'Erreur lors de la suppression');
        return false;
      }
    } catch (error: any) {
      console.error('❌ [useSupplierBills] Erreur deleteSupplierBill:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // État
    loading,
    supplierBills,
    currentSupplierBill,

    // Actions
    fetchSupplierBillsByShipment,
    fetchSupplierBillById,
    recordSupplierBill,
    deleteSupplierBill,
  };
};

export default useSupplierBills;
